import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Phone, Loader2, User } from 'lucide-react';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { trackSignupConversion, trackEvent } from '@/lib/gtag';
import { getAnalyticsContext } from '@/lib/utm';


interface PhoneAuthFormProps {
  onSuccess: () => void;
  mode?: 'signup' | 'login';
}

export function PhoneAuthForm({ onSuccess, mode = 'login' }: PhoneAuthFormProps) {
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [step, setStep] = useState<'phone' | 'code' | 'name'>('phone');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    const limited = numbers.slice(0, 11);
    
    if (limited.length <= 2) return limited;
    if (limited.length <= 7) return `(${limited.slice(0, 2)}) ${limited.slice(2)}`;
    return `(${limited.slice(0, 2)}) ${limited.slice(2, 7)}-${limited.slice(7)}`;
  };

  const getFullPhone = () => {
    const numbers = phone.replace(/\D/g, '');
    return `+55${numbers}`;
  };

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const numbers = phone.replace(/\D/g, '');
    if (numbers.length < 10) {
      toast.error('Digite um número de telefone válido');
      return;
    }

    setLoading(true);
    trackEvent(mode === 'login' ? 'login_started' : 'signup_started', { ...getAnalyticsContext(), method: 'sms' });


    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone: getFullPhone() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Erro ao enviar código');
        return;
      }

      toast.success('Código enviado por SMS!');
      setStep('code');
      setCountdown(60);
    } catch (error) {
      console.error('Error sending OTP:', error);
      toast.error('Erro ao enviar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone: getFullPhone(), code }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Código inválido');
        return;
      }

      if (data.session) {
        await supabase.auth.setSession(data.session);
        
        if (data.isNewUser) {
          trackSignupConversion();
          trackEvent('signup_completed', { ...getAnalyticsContext(), method: 'sms' });
          setStep('name');
        } else {
          toast.success('Login realizado com sucesso!');
          onSuccess();
        }

      } else {
        toast.error('Erro ao fazer login');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('Erro ao verificar código. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedName = name.trim();
    if (trimmedName.length < 2) {
      toast.error('Digite seu nome (mínimo 2 caracteres)');
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error('Erro ao obter usuário');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .update({ display_name: trimmedName })
        .eq('user_id', user.id);

      if (error) {
        console.error('Error saving name:', error);
        toast.error('Erro ao salvar nome');
        return;
      }

      toast.success('Bem-vindo ao VerdeIA!');
      onSuccess();
    } catch (error) {
      console.error('Error saving name:', error);
      toast.error('Erro ao salvar nome. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-otp`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ phone: getFullPhone() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Erro ao reenviar código');
        return;
      }

      toast.success('Código reenviado!');
      setCountdown(60);
    } catch (error) {
      toast.error('Erro ao reenviar código');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'name') {
    return (
      <form onSubmit={handleSaveName} className="space-y-4">
        <div className="text-center mb-4">
          <div className="text-3xl mb-2">🎉</div>
          <h3 className="text-lg font-semibold text-foreground">Bem-vindo!</h3>
          <p className="text-sm text-muted-foreground">
            Como você gostaria de ser chamado?
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name" className="text-foreground">Seu nome</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="name"
              type="text"
              placeholder="Digite seu nome"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-10"
              maxLength={50}
              autoFocus
            />
          </div>
        </div>

        <Button
          type="submit"
          className="w-full gradient-nature text-primary-foreground"
          disabled={loading || name.trim().length < 2}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Salvando...
            </>
          ) : (
            'Continuar'
          )}
        </Button>

        <p className="text-xs text-center text-muted-foreground">
          Você pode alterar isso depois nas configurações
        </p>
      </form>
    );
  }

  if (step === 'code') {
    return (
      <form onSubmit={handleVerifyCode} className="space-y-4">
        <div className="text-center mb-4">
          <p className="text-sm text-muted-foreground">
            Digite o código enviado para
          </p>
          <p className="font-medium text-foreground">{phone}</p>
        </div>

        <div className="flex justify-center">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={(value) => setCode(value)}
          >
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <Button
          type="submit"
          className="w-full gradient-nature text-primary-foreground"
          disabled={loading || code.length !== 6}
        >
          {loading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Verificando...
            </>
          ) : (
            'Verificar código'
          )}
        </Button>

        <div className="text-center space-y-2">
          <button
            type="button"
            onClick={handleResendCode}
            disabled={countdown > 0 || loading}
            className="text-sm text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
          >
            {countdown > 0 ? `Reenviar em ${countdown}s` : 'Reenviar código'}
          </button>
          
          <button
            type="button"
            onClick={() => {
              setStep('phone');
              setCode('');
            }}
            className="block w-full text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            Alterar número
          </button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSendCode} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="phone" className="text-foreground">Telefone</Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="phone"
            type="tel"
            placeholder="(00) 00000-0000"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            className="pl-10"
            required
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {mode === 'signup'
            ? 'Sua conta é criada automaticamente ao verificar o código por SMS.'
            : 'Você receberá um código de verificação por SMS'}
        </p>
      </div>

      <Button
        type="submit"
        className="w-full gradient-nature text-primary-foreground"
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Enviando...
          </>
        ) : (
          'Receber código por SMS'
        )}
      </Button>
    </form>
  );
}
